import React, { useEffect, useState } from "react";
import NetwalaModal from "../Items/Modal/Index";
import RechargeIcon from "./../../assets/svgs/RechargeIcon.svg";
import { Col, Row, Typography, Form, Input } from "antd";
import moment from "moment";
import "./styles/RequestIPTVModal.css";
import NetwalaButton from "../Items/Button/Index";
import { ButtonProps } from "../../helpers/constants";
import useAxiosPost from "../../hooks/useAxiosPost";
import useMultiNotification from "../../hooks/useNotification";
import NetwalaSelect from "../Items/Select/Index";

interface ActivateInternetConnectionModalProps {
  show: boolean;
  handleClose: (reload: boolean) => void;
  connection: SingleConnectionApiResponse;
}

const ActivateInternetConnectionModal: React.FC<
  ActivateInternetConnectionModalProps
> = ({ show, handleClose, connection }) => {
  const { openNotification, contextHolder } = useMultiNotification();
  const [form] = Form.useForm();

  const [
    getPackagesApi,
    packagesApiResponse,
    packagesApiError,
    packagesApiLoading,
    packagesApiMessage,
    packagesApiErrorCount,
  ] = useAxiosPost<PackagesAndVlansByISPApiProp>("packages/getPackagesByISP");
  const [
    getVlansApi,
    vlansApiResponse,
    vlansApiError,
    vlansApiLoading,
    vlansApiMessage,
    vlansApiErrorCount,
  ] = useAxiosPost<PackagesAndVlansByISPApiProp>("isps/vlans/getVLANsByISP");
  const [
    postActivateApi,
    activateApiResponse,
    activateApiError,
    activateApiLoading,
    activateApiMessage,
    activateApiErrorCount,
  ] = useAxiosPost<any>("connections/activateInternetConnection", true);

  const [packages, setPackages] = useState<NetwalaSelectOptions[]>([]);
  const [vlans, setVlans] = useState<NetwalaSelectOptions[]>([]);
  const [allPackages, setAllPackages] = useState<any[]>([]);
  const [selectedPackagePrice, setSelectedPackagePrice] = useState<
    number | null
  >(null);
  const [calculatedExpiryDate, setCalculatedExpiryDate] = useState<string>("");

  useEffect(() => {
    if (show && connection.intPkIspID) {
      // Automatically fetch packages and VLANs using the connection's ISP
      getPackagesApi({ ispId: Number(connection.intPkIspID) });
      getVlansApi({ ispId: Number(connection.intPkIspID) });
      form.resetFields();
      setCalculatedExpiryDate("");
      setSelectedPackagePrice(null);
      setPackages([]);
      setVlans([]);
      setAllPackages([]);
    }
  }, [show, connection.intPkIspID]);

  useEffect(() => {
    if (packagesApiResponse !== null) {
      const data: any = packagesApiResponse.responseData;
      const packagesOptions = data.map((item: any): NetwalaSelectOptions => {
        return {
          value: item.intPkPackageID,
          label: item.packageName,
        };
      });
      setAllPackages(data || []);
      setPackages(packagesOptions);
    }
  }, [packagesApiResponse]);

  useEffect(() => {
    if (vlansApiResponse !== null) {
      const data: any = vlansApiResponse.responseData;
      const vlansOptions = data.map((item: any): NetwalaSelectOptions => {
        return {
          value: item.intPkVlanID,
          label: item.vlanValue,
        };
      });
      setVlans(vlansOptions);
    }
  }, [vlansApiResponse]);

  useEffect(() => {
    if (activateApiResponse !== null) {
      const { message } = activateApiResponse;
      if (message) {
        openNotification(`success`, "Success", message);
        setTimeout(() => {
          handleClose(true);
        }, 2500);
      }
    }
  }, [activateApiResponse]);

  useEffect(() => {
    if (activateApiError !== null && activateApiErrorCount !== 0) {
      openNotification(`error`, "Activation Failed", activateApiError);
    }
  }, [activateApiError, activateApiErrorCount]);

  const handlePackageChange = (value: string) => {
    const found = allPackages.find(
      (item: any) => Number(item.intPkPackageID) === Number(value)
    );
    if (found) {
      setSelectedPackagePrice(found.salePrice);
      form.setFieldsValue({ amountPaid: found.salePrice || 0 });

      // Calculate expiry date based on package duration
      const dateFormat = "YYYY-MM-DD";
      let expiryDate = moment();

      if (found.billingMethod === "MONTHLY") {
        // If monthly, add 1 month
        expiryDate = expiryDate.add(1, "month");
      } else if (found.durationDays) {
        // If durationDays is specified, add those days
        expiryDate = expiryDate.add(Number(found.durationDays), "days");
      } else {
        // Default to 1 month if no duration specified
        expiryDate = expiryDate.add(1, "month");
      }

      setCalculatedExpiryDate(expiryDate.format(dateFormat));
    }
  };

  const onFinish = (values: any) => {
    if (!calculatedExpiryDate) {
      openNotification(
        `error`,
        "Error",
        "Please select a package to calculate expiry date"
      );
      return;
    }

    const activationData = {
      intPkConnectionID: connection.intPkConnectionID,
      intPkPackageID: Number(values.package),
      intPkVlanID: values.vlan ? Number(values.vlan) : null,
      expiryDate: calculatedExpiryDate,
      voucherNumber: values.voucherNumber || "",
      ontID: values.ontID ? Number(values.ontID) : null,
      amountPaid: Number(values.amountPaid),
    };

    postActivateApi(activationData);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };

  return (
    <>
      {contextHolder}
      <NetwalaModal
        title="Activate Internet Connection"
        titleImage={RechargeIcon}
        headerStyle={{
          backgroundColor: "#292D8E",
          color: "#FFFFFF",
          fontSize: "16px",
          fontWeight: "400",
          height: "50px",
        }}
        open={show}
        onCancel={() => handleClose(false)}
      >
        <Row>
          <Col span={24} className="iptv-modal-package-details">
            <Form
              form={form}
              requiredMark={false}
              style={{ width: "100%" }}
              layout="vertical"
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              name="ActivateConnectionForm"
              initialValues={{
                amountPaid: 0,
              }}
            >
              {connection.intPkIspID && connection.ISPName && (
                <Form.Item
                  label={<span className="create-user-form-label">ISP</span>}
                >
                  <Input
                    disabled
                    className="user-input-general-height"
                    value={connection.ISPName}
                  />
                </Form.Item>
              )}

              <Form.Item
                name="package"
                rules={[{ required: true, message: "Please Select Package" }]}
                label={<span className="create-user-form-label">Package</span>}
              >
                <NetwalaSelect
                  showSearch
                  onChange={handlePackageChange}
                  size="large"
                  height="45px"
                  options={packages}
                />
              </Form.Item>

              {selectedPackagePrice && (
                <Form.Item
                  label={
                    <span className="create-user-form-label">
                      Package Price
                    </span>
                  }
                >
                  <Input
                    disabled
                    className="user-input-general-height"
                    value={selectedPackagePrice}
                  />
                </Form.Item>
              )}

              <Form.Item
                name="vlan"
                label={<span className="create-user-form-label">VLAN</span>}
              >
                <NetwalaSelect
                  showSearch
                  size="large"
                  height="45px"
                  options={vlans}
                />
              </Form.Item>

              {calculatedExpiryDate && (
                <Form.Item
                  label={
                    <span className="create-user-form-label">Expiry Date</span>
                  }
                >
                  <Input
                    disabled
                    className="user-input-general-height"
                    value={calculatedExpiryDate}
                  />
                </Form.Item>
              )}

              <Form.Item
                name="amountPaid"
                rules={[
                  { required: true, message: "Please enter Amount Paid" },
                  {
                    validator: (_, value) =>
                      value >= 0
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error(
                              "Amount Paid must be greater than or equal to zero"
                            )
                          ),
                  },
                ]}
                label={
                  <span className="create-user-form-label">Amount Paid</span>
                }
              >
                <Input
                  className="user-input-general-height"
                  placeholder="0"
                  type="number"
                />
              </Form.Item>

              <Form.Item
                name="ontID"
                label={<span className="create-user-form-label">ONT ID</span>}
              >
                <Input
                  className="user-input-general-height"
                  placeholder="ONT ID"
                  type="number"
                />
              </Form.Item>

              <Form.Item
                name="voucherNumber"
                label={
                  <span className="create-user-form-label">Voucher Number</span>
                }
              >
                <Input
                  className="user-input-general-height"
                  placeholder="ABCDEFG21"
                />
              </Form.Item>

              <Form.Item>
                <NetwalaButton
                  backgroundColor={ButtonProps.background.YELLOW}
                  height={ButtonProps.height.STANDARD}
                  width={`100%`}
                  textColor={ButtonProps.color.STANDARD}
                  onClick={() => {}}
                  htmlType="submit"
                  text="Activate Connection"
                  borderColor="#FFA726"
                  disabled={activateApiLoading}
                />
              </Form.Item>
            </Form>
          </Col>
        </Row>
      </NetwalaModal>
    </>
  );
};

export default ActivateInternetConnectionModal;
